import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { ITemplate, ISport, IMarket } from '../../settings.types';
import { ApiService } from 'app/core/api/api.service';
import { TranslocoService } from '@ngneat/transloco';
import { map, switchMap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class TemplateService {
    private tempIdCounter = 0;
    private sportsSubject = new BehaviorSubject<ISport[]>([]);

  
    constructor(
        private _apiService: ApiService,
        private translocoService: TranslocoService
    ) {
        this.translocoService.langChanges$.subscribe(() => {
            this.updateSports();
        });

        this.updateSports();
    }

    private updateSports(): void {
        const updatedSports: ISport[] = [
            {
                id: 'sr:sport:1',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.soccer'
                ),
            },
            {
                id: 'sr:sport:2',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.basketball'
                ),
            },
            {
                id: 'sr:sport:3',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.baseball'
                ),
            },
            {
                id: 'sr:sport:4',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.iceHockey'
                ),
            },
            {
                id: 'sr:sport:5',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.tennis'
                ),
            },
            {
                id: 'sr:sport:6',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.handball'
                ),
            },
            {
                id: 'sr:sport:12',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.rugby'
                ),
            },
            {
                id: 'sr:sport:13',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.aussieRules'
                ),
            },
            {
                id: 'sr:sport:16',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.americanFootball'
                ),
            },
            {
                id: 'sr:sport:19',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.snooker'
                ),
            },
            {
                id: 'sr:sport:20',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.tableTennis'
                ),
            },
            {
                id: 'sr:sport:21',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.cricket'
                ),
            },
            {
                id: 'sr:sport:22',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.darts'
                ),
            },
            {
                id: 'sr:sport:23',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.volleyball'
                ),
            },
            {
                id: 'sr:sport:24',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.fieldHockey'
                ),
            },
            {
                id: 'sr:sport:29',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.futsal'
                ),
            },
            {
                id: 'sr:sport:31',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.badminton'
                ),
            },
            {
                id: 'sr:sport:32',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.bowls'
                ),
            },
            {
                id: 'sr:sport:34',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.beachVolley'
                ),
            },
            {
                id: 'sr:sport:37',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.squash'
                ),
            },
            {
                id: 'sr:sport:117',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.mma'
                ),
            },
            {
                id: 'sr:sport:137',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.eSoccer'
                ),
            },
            {
                id: 'sr:sport:138',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.kabaddi'
                ),
            },
            {
                id: 'sr:sport:40',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.formula1'
                ),
            },
            {
                id: 'sr:sport:190',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.motorcycleRacing'
                ),
            },
            {
                id: 'sr:sport:9',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.golf'
                ),
            },
            {
                id: 'sr:sport:43',
                name: this.translocoService.translate(
                    'settings.template-editor.sports.alpineSkiing'
                ),
            },
        ];

        this.sportsSubject.next(updatedSports);
    }

    getSports(): Observable<ISport[]> {
        return this.sportsSubject.asObservable();
    }
    getSportsValue(): ISport[] {
        return this.sportsSubject.value;
    }

    getTemplates(sport: string): Observable<ITemplate[]> {
        return this._apiService.get<ITemplate[]>(
            'admin/template?type=sidePanel&sportId=' + sport
        );
    }

    createTemplate(
        template: Omit<ITemplate, 'id'>,
        priority
    ): Observable<ITemplate> {
        const newTemplate = {
            ...template,
            id: `temp_${this.tempIdCounter++}`,
            priority: priority,
        };
        return of(newTemplate);
    }

    saveTemplate(template: ITemplate): Observable<ITemplate> {
        delete template.hasUnsavedChanges;
        if (String(template.id).startsWith('temp_')) {
            delete template.id;
            return this._apiService.post('admin/template', template);
        } else {
            return this._apiService.put(
                'admin/template/' + template.id,
                template
            );
        }
    }

    updatePriorities(
        templates: { id: string; priority: number }[]
    ): Observable<void> {
        return this._apiService.put('admin/template-priority', templates);
    }

    deleteTemplate(templateId: string): Observable<void> {
        return this._apiService.delete(`admin/template/${templateId}`);
    }    

    searchEvents(
        searchQuery: string,
        sportId: string,
        type: string = 'event'
    ): Observable<any[]> {
        return this._apiService.get<any[]>(`admin/search`, {
            searchQuery,
            sportId,
            type,
        });
    }

    searchMarkets(searchQuery: string, sportId: string): Observable<any[]> {
        return this._apiService.get<any[]>(
            `admin/search?searchQuery=${searchQuery}&sportId=${sportId}&type=market`
        );
    }
    
    getSelectedMarketsBySportType(sportId: string): Observable<ITemplate[]> {
        const typePattern = `${sportId}-markets`;
        return this._apiService.get<ITemplate[]>(
            `admin/template?type=${typePattern}&sportId=${sportId}`
        ).pipe(
            map((templates) => {
                return templates.map(template => ({
                    ...template,
                    markets: template.markets || [],
                }));
            })
        );
    }

    deleteMarketFromTemplate(templateId: number, marketId: string): Observable<any> {
        return this._apiService.get<ITemplate>(`admin/template/${templateId}`).pipe(
          map((template) => {
            const updatedMarkets = template.markets.filter(
              (market) => market.id !== marketId
            );
            const updatedTemplate: ITemplate = { ...template, markets: updatedMarkets };
            return updatedTemplate;
          }),
          switchMap((updatedTemplate) =>
            this._apiService.put(`admin/template/${templateId}`, updatedTemplate)
          )
        );
      }
    
      updateMarketsInTemplate(templateId: number, newMarket: IMarket): Observable<any> {
        return this._apiService.get<ITemplate>(`admin/template/${templateId}`).pipe(
          map((template) => {
            const updatedMarkets = [...template.markets, newMarket];
            const updatedTemplate: ITemplate = { ...template, markets: updatedMarkets };
            return updatedTemplate;
          }),
          switchMap((updatedTemplate) =>
            this._apiService.put(`admin/template/${templateId}`, updatedTemplate)
          )
        );
      }
    
      saveTemplateWithMarkets(template: ITemplate): Observable<ITemplate> {
        return this._apiService.put<ITemplate>(`admin/template/${template.id}`, template);
      }
}